import type { FunctionComponent, ReactNode } from 'react';
import { useEffect, useMemo } from 'react';
import { useStore } from 'zustand';

import { useTranslate } from '~/shared/context/use-translate';
import type { LanguageCodeForJSA } from '~/shared/internationalization';
import { CheckIcon } from '~/ui/core/icons';
import { SCROLL_FADE_HEADER_PROPS } from '~/ui/core/scroll-fade/ScrollFade';
import { ItemIndicator, ItemText, Select, SelectItem, StyledItem } from '~/ui/core/select/Select';

import { useProfileTaskDraftCoordinator } from '../draft/ProfileTaskDraftCoordinatorProvider';
import { getProfileTaskParentDraftKey } from '../draft/profile-task-draft-coordinator';
import { createProfileTaskVersionInput } from '../draft/profile-task-draft-mappers';
import type {
    ProfileTask,
    ProfileTaskEffort,
    ProfileTaskParentContext,
    ProfileTaskVersionInput,
} from '../model/profile-task';
import {
    getProfileTaskClassificationDescriptionTranslationKey,
    getProfileTaskClassificationTranslationKey,
    profileTaskTranslationKeys,
} from '../profile-task-translations';
import { validateProfileTask } from '../validation/profile-task-validation';
import { ProfileTaskBreadcrumbs } from './ProfileTaskBreadcrumbs';
import type { ProfileTaskMetaRow } from './ProfileTaskFields';
import { ProfileTaskMetaRows } from './ProfileTaskFields';
import { ProfileTaskRichTextField } from './ProfileTaskRichTextField';
import { TaskSkillSelector } from './TaskSkillSelector';
import { TaskTitleEditable } from './TaskTitleEditable';
import { TaskWorkflowStepsEditor } from './TaskWorkflowStepsEditor';

const classificationOptions = ['commodity', 'contribution'] as const;

const presetEffortRanges: readonly ProfileTaskEffort[] = [
    { maximumHoursPerWeek: 4, minimumHoursPerWeek: 0 },
    { maximumHoursPerWeek: 8, minimumHoursPerWeek: 4 },
    { maximumHoursPerWeek: 12, minimumHoursPerWeek: 8 },
    { maximumHoursPerWeek: 20, minimumHoursPerWeek: 12 },
    { maximumHoursPerWeek: 40, minimumHoursPerWeek: 20 },
];

const getEffortRangeValue = (effort: ProfileTaskEffort): string =>
    `${effort.minimumHoursPerWeek ?? ''}:${effort.maximumHoursPerWeek ?? ''}`;

export const ProfileTaskForm: FunctionComponent<{
    readonly activeLanguage: LanguageCodeForJSA;
    readonly autoFocusTitle?: boolean;
    readonly context: ProfileTaskParentContext;
    readonly renderHeader: () => ReactNode;
    readonly task?: ProfileTask;
    readonly taskId: string;
}> = ({ activeLanguage, autoFocusTitle = false, context, renderHeader, task, taskId }) => {
    const coordinator = useProfileTaskDraftCoordinator();
    const translate = useTranslate();
    const parentKey = getProfileTaskParentDraftKey(context);
    const editor = useStore(coordinator, (state) => state.parents.get(parentKey)?.editors.get(taskId));
    const appliedReference = useStore(coordinator, (state) =>
        state.parents.get(parentKey)?.applied.find((reference) => reference.taskId === taskId),
    );
    const initialForm = useMemo(
        () =>
            appliedReference?.draft ??
            (task === undefined ? undefined : createProfileTaskVersionInput(task, activeLanguage)),
        [activeLanguage, appliedReference?.draft, task],
    );

    useEffect(() => {
        if (editor !== undefined || initialForm === undefined) {
            return;
        }
        coordinator.getState().openEditor({
            context,
            form: initialForm,
            sourceTaskVersionId: task?.taskVersionId,
            taskId,
        });
    }, [context, coordinator, editor, initialForm, task?.taskVersionId, taskId]);

    if (editor === undefined) {
        return null;
    }

    const validation = validateProfileTask(editor.form);
    const update = (form: ProfileTaskVersionInput): void => coordinator.getState().updateEditor(context, taskId, form);
    const validationMessage = (field: string): string | undefined => {
        if (validation.ok) {
            return undefined;
        }
        const issue = validation.issues.find((candidate) => candidate.field === field);
        return issue === undefined ? undefined : translate(issue.translationKey);
    };
    const titleValidationMessage = validationMessage('title');
    const effortValidationMessage =
        validationMessage('effort.minimum') ?? validationMessage('effort.maximum') ?? validationMessage('effort.range');
    const currentEffortValue = editor.form.effort === undefined ? undefined : getEffortRangeValue(editor.form.effort);
    const effortRanges =
        editor.form.effort === undefined ||
        presetEffortRanges.some((effort) => getEffortRangeValue(effort) === currentEffortValue)
            ? presetEffortRanges
            : [editor.form.effort, ...presetEffortRanges];
    const availableClassifications =
        editor.form.classification === undefined ||
        classificationOptions.some((classification) => classification === editor.form.classification)
            ? classificationOptions
            : [editor.form.classification, ...classificationOptions];
    // A manual pick invalidates the generated reasoning, so it is cleared alongside the value.
    const metaRows: readonly ProfileTaskMetaRow[] = [
        {
            key: 'classification',
            label: translate(profileTaskTranslationKeys.classification),
            value: (
                <Select
                    aria-label={translate(profileTaskTranslationKeys.classification)}
                    placeholder={translate(profileTaskTranslationKeys.classification)}
                    value={editor.form.classification}
                    onValueChange={(value) => {
                        if (value === editor.form.classification) {
                            return;
                        }

                        update({
                            ...editor.form,
                            classification: classificationOptions.find((candidate) => candidate === value),
                            classificationReasoning: '',
                        });
                    }}
                >
                    {availableClassifications.map((classification) => {
                        const descriptionKey = getProfileTaskClassificationDescriptionTranslationKey(classification);

                        return (
                            <StyledItem key={classification} value={classification}>
                                <div className="flex min-w-0 flex-1 flex-col gap-xxs">
                                    <span className="text-body-s font-medium leading-normal text-text-primary">
                                        {/* Only the label may live inside ItemText: Radix clones its children into the closed trigger. */}
                                        <ItemText>
                                            {classificationOptions.some((candidate) => candidate === classification)
                                                ? translate(getProfileTaskClassificationTranslationKey(classification))
                                                : classification}
                                        </ItemText>
                                    </span>
                                    {descriptionKey === undefined ? null : (
                                        <span className="max-w-[18rem] text-text-secondary">
                                            {translate(descriptionKey)}
                                        </span>
                                    )}
                                </div>
                                <ItemIndicator className="ml-s inline-flex items-center justify-center text-text-secondary">
                                    <CheckIcon />
                                </ItemIndicator>
                            </StyledItem>
                        );
                    })}
                </Select>
            ),
        },
        {
            key: 'effort',
            label: translate(profileTaskTranslationKeys.effort),
            value: (
                <div className="flex flex-col gap-s">
                    <Select
                        aria-label={translate(profileTaskTranslationKeys.effort)}
                        placeholder={translate(profileTaskTranslationKeys.effort)}
                        value={currentEffortValue}
                        variant={effortValidationMessage === undefined ? 'default' : 'error'}
                        onValueChange={(value) => {
                            const effort = effortRanges.find((candidate) => getEffortRangeValue(candidate) === value);
                            if (effort === undefined || value === currentEffortValue) {
                                return;
                            }

                            update({ ...editor.form, effort, effortReasoning: '' });
                        }}
                    >
                        {effortRanges.map((effort) => (
                            <SelectItem key={getEffortRangeValue(effort)} value={getEffortRangeValue(effort)}>
                                {translate(profileTaskTranslationKeys.effortFormat, {
                                    maximum: effort.maximumHoursPerWeek,
                                    minimum: effort.minimumHoursPerWeek,
                                })}
                            </SelectItem>
                        ))}
                    </Select>
                    {effortValidationMessage === undefined ? null : (
                        <span className="text-body-xs font-normal text-text-error">{effortValidationMessage}</span>
                    )}
                </div>
            ),
        },
    ];

    return (
        <div className="flex w-full flex-col gap-3xl" data-testid="profile-task-form">
            <div className="sticky top-0 z-[2] bg-surface-raised" {...SCROLL_FADE_HEADER_PROPS}>
                {renderHeader()}
            </div>

            <ProfileTaskBreadcrumbs context={context} />

            <div className="flex flex-col gap-s">
                <TaskTitleEditable
                    autoFocus={autoFocusTitle}
                    placeholder={translate(profileTaskTranslationKeys.taskTitle)}
                    value={editor.form.title}
                    onChange={(title) => update({ ...editor.form, title })}
                />
                {titleValidationMessage === undefined ? null : (
                    <span className="text-body-xs font-normal text-text-error">{titleValidationMessage}</span>
                )}
            </div>

            <ProfileTaskRichTextField
                field="description"
                label={translate(profileTaskTranslationKeys.description)}
                labelHidden={true}
                taskId={taskId}
                testId="profile-task-description-input"
                value={editor.form.description}
                onChange={(description) => update({ ...editor.form, description })}
            />

            <TaskWorkflowStepsEditor
                hasIssue={(field, workflowStepId) =>
                    !validation.ok &&
                    validation.issues.some((issue) => issue.field === field && issue.workflowStepId === workflowStepId)
                }
                steps={editor.form.workflowSteps}
                taskId={taskId}
                onChange={(workflowSteps) => update({ ...editor.form, workflowSteps })}
            />

            <ProfileTaskMetaRows rows={metaRows} />

            <TaskSkillSelector
                activeLanguage={activeLanguage}
                knownSkills={task?.requiredSkills ?? []}
                selectedSkillIds={editor.form.requiredSkillIds}
                onChange={(requiredSkillIds) => update({ ...editor.form, requiredSkillIds })}
            />
        </div>
    );
};
